import { Link } from "react-router-dom";
import Seo from "@/components/base/Seo";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import SectionHeading from "@/components/base/SectionHeading";
import Rating from "@/components/base/Rating";
import { SITE } from "@/config/site";
import { whyUsPoints, testimonials } from "@/mocks/siteContent";

const reasons = [
  {
    id: "r-1",
    icon: "ri-map-pin-2-line",
    title: "Hyperlocal to the last locality",
    text: "Results are tied to the actual area inside the district, so your customers see nearby businesses first — not a pin ten kilometres away.",
  },
  {
    id: "r-2",
    icon: "ri-shield-check-line",
    title: "Verification you can trust",
    text: "Premium and Elite listings go through a manual address and owner check. What you see is what you can actually call.",
  },
  {
    id: "r-3",
    icon: "ri-equalizer-line",
    title: "Filters that respect your time",
    text: "Combine keyword, category, location, rating and opening-hours filters and land on the right shortlist in seconds.",
  },
  {
    id: "r-4",
    icon: "ri-money-rupee-circle-line",
    title: "Fair pricing for owners",
    text: "A genuinely useful free listing, with transparent upgrades when you want more reach. No hidden pay-to-rank games.",
  },
  {
    id: "r-5",
    icon: "ri-line-chart-line",
    title: "An owner dashboard that works",
    text: "Edit your details, manage photos and reviews, and see the views and calls you're getting — all in one place.",
  },
  {
    id: "r-6",
    icon: "ri-customer-service-2-line",
    title: "Real local support",
    text: "A Mysore-based team ready to help you set up, optimise and get the most out of your listing, in your language.",
  },
];

const comparison = [
  { id: "c-1", feature: "Locality-level coverage across the Mysore region", us: true, them: false },
  { id: "c-2", feature: "Combined keyword + category + location + rating search", us: true, them: false },
  { id: "c-3", feature: "Manually verified premium listings", us: true, them: false },
  { id: "c-4", feature: "Owner dashboard with performance analytics", us: true, them: false },
  { id: "c-5", feature: "Tourist spots alongside local services", us: true, them: false },
  { id: "c-6", feature: "Clearly labelled featured placement", us: true, them: true },
];

export default function WhyUs() {
  return (
    <>
      <Seo
        title={`Why ${SITE.name} — Built for Local Discovery`}
        description={`See why owners and customers choose ${SITE.name}: hyperlocal search, verified listings, fair pricing and a dashboard that actually helps local businesses grow.`}
        keywords="why Mysore Yellow Pages, best local directory Karnataka, verified business listings"
        path="/why-us"
      />

      <div className="border-b border-background-200 bg-background-100/70">
        <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-8 md:py-12">
          <Breadcrumbs items={[{ label: "Why Us" }]} />
          <span className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-accent-900">
            <i className="ri-thumb-up-line"></i> Why Mysore Yellow Pages
          </span>
          <h1 className="mt-4 max-w-3xl font-heading text-3xl md:text-5xl font-extrabold leading-tight tracking-tight text-foreground-950">
            Why locals search here — and owners stay
          </h1>
          <p className="mt-4 max-w-2xl text-sm md:text-lg text-foreground-600 leading-relaxed">
            A directory is only as good as its data and its search. Here's what makes {SITE.name} different from a
            generic listing site.
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-12 md:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {reasons.map((r) => (
            <div key={r.id} className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                <i className={`${r.icon} text-xl`}></i>
              </span>
              <h2 className="mt-4 font-heading text-base font-bold text-foreground-950">{r.title}</h2>
              <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{r.text}</p>
            </div>
          ))}
        </div>
      </div>

      <section className="border-y border-background-200 bg-background-100/70">
        <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-12 md:py-16">
          <SectionHeading
            eyebrow="The comparison"
            title="How we stack up"
            subtitle="What you get with a Mysore Yellow Pages listing versus a typical generic directory."
          />
          <div className="mt-8 overflow-hidden rounded-lg border border-background-200 bg-background-50">
            <div className="grid grid-cols-[1fr_auto_auto] items-center gap-4 border-b border-background-100 bg-background-100/60 px-5 py-3 text-xs font-bold uppercase tracking-wide text-foreground-500">
              <span>What you get</span>
              <span className="w-20 text-center text-primary-800">Us</span>
              <span className="w-20 text-center">Others</span>
            </div>
            {comparison.map((row) => (
              <div key={row.id} className="grid grid-cols-[1fr_auto_auto] items-center gap-4 border-b border-background-100 px-5 py-3.5 last:border-0 text-sm">
                <span className="text-foreground-800">{row.feature}</span>
                <span className="flex w-20 justify-center">
                  {row.us ? (
                    <i className="ri-check-line text-lg font-bold text-accent-600"></i>
                  ) : (
                    <i className="ri-close-line text-lg text-foreground-300"></i>
                  )}
                </span>
                <span className="flex w-20 justify-center">
                  {row.them ? (
                    <i className="ri-check-line text-lg font-bold text-foreground-400"></i>
                  ) : (
                    <i className="ri-close-line text-lg text-foreground-300"></i>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10">
          <div className="rounded-lg border border-primary-200 bg-primary-50 p-6 md:p-8">
            <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-500 text-foreground-950">
              <i className="ri-user-search-line text-xl"></i>
            </span>
            <h2 className="mt-4 font-heading text-xl font-extrabold text-foreground-950">For customers</h2>
            <ul className="mt-4 space-y-3 text-sm text-foreground-700">
              {[
                "Find nearby businesses by keyword, category and locality",
                "Filter by rating and whether they're open right now",
                "See real photos, hours, contact details and reviews",
                "Discover tourist spots alongside everyday services",
              ].map((t) => (
                <li key={t} className="flex gap-2.5">
                  <i className="ri-checkbox-circle-line mt-0.5 text-accent-600"></i> {t}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
            <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-secondary-500 text-background-50">
              <i className="ri-store-2-line text-xl"></i>
            </span>
            <h2 className="mt-4 font-heading text-xl font-extrabold text-foreground-950">For owners</h2>
            <ul className="mt-4 space-y-3 text-sm text-foreground-700">
              {[
                "A free listing with full control over your details",
                "Verified badges that build instant trust",
                "Priority placement on category and homepage features",
                "Analytics showing your views, calls and enquiries",
              ].map((t) => (
                <li key={t} className="flex gap-2.5">
                  <i className="ri-checkbox-circle-line mt-0.5 text-accent-600"></i> {t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <section className="border-y border-background-200 bg-background-100/70">
        <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-12 md:py-16">
          <SectionHeading eyebrow="Proof" title="Owners already growing with us" />
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            {testimonials.map((t) => (
              <figure key={t.id} className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-5">
                <i className="ri-double-quotes-l text-2xl text-primary-400"></i>
                <blockquote className="mt-3 flex-1 text-sm text-foreground-700 leading-relaxed">{t.text}</blockquote>
                <Rating value={t.rating} />
                <figcaption className="mt-4 border-t border-background-100 pt-3">
                  <span className="block font-heading text-sm font-bold text-foreground-950">{t.name}</span>
                  <span className="block text-xs text-foreground-500">{t.role}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-12 md:py-16">
        <div className="rounded-lg bg-accent-900 p-6 md:p-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-500 px-3 py-1 text-xs font-bold text-foreground-950">
              <i className="ri-rocket-line"></i> Get started free
            </span>
            <h2 className="mt-4 font-heading text-xl md:text-3xl font-extrabold text-background-50">
              Put your business in front of the right people
            </h2>
            <p className="mt-2 max-w-xl text-sm md:text-base text-accent-100/90">
              Create a free listing in minutes, then upgrade whenever you're ready for a verified badge and priority placement.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/list-your-business"
              className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-3 text-sm font-bold text-foreground-950 hover:bg-primary-600 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-add-circle-line"></i> List your business
            </Link>
            <Link
              to="/listing-plans"
              className="inline-flex items-center gap-2 rounded-md border border-accent-100/40 bg-accent-50/10 px-5 py-3 text-sm font-semibold text-background-50 hover:bg-accent-50/20 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-price-tag-3-line"></i> View plans
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}